/**
 * User Store - Zustand 기반 클라이언트 인증 상태 관리
 *
 * 통합 기능:
 * - Supabase 세션 + DB 쿼리 기반 사용자 정보 관리
 * - Server Action (getCurrentUser) 호출로 최신 데이터 조회
 * - 반응형 훅 (useAuth) 및 비반응형 클래스 (ClientAuth) 제공
 */

"use client";

import { create } from "zustand";
import { getCurrentUser } from "@/lib/auth/actions/user-actions";
import { UserRole } from "@/types/enums";
import { signOut as authSignOut } from "@/lib/services/auth";
import type { PermissionData } from "@/lib/auth/core/types";
import { diagnoseActionFailure } from "@/lib/debug/action-diagnostic";

/**
 * v_user_permissions View 데이터 구조
 */
interface UserData extends PermissionData {
  center_name: string | null;
  center_status: "lead" | "active" | "inactive" | null;
}

interface UserStore {
  // State
  user: UserData | null;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;

  // Actions
  setUser: (user: UserData | null) => void;
  setLoading: (loading: boolean) => void;
  setInitialized: (initialized: boolean) => void;
  setError: (error: string | null) => void;
  signOut: () => Promise<void>;

  // Data fetching
  refreshUserData: (forceRefresh?: boolean) => Promise<void>;
  clearUserData: () => void;

  // Computed helpers
  isAuthenticated: () => boolean;
  hasRole: (role: UserRole) => boolean;
  isCenterAdmin: () => boolean;
  isBranchAdmin: () => boolean;
  getCenterId: () => number | null;
  getBranchId: () => string | null;
  getUserId: () => string | null;

  // Impersonation helpers (v2.0)
  isImpersonating: () => boolean;
  getOriginalCenterId: () => number | null;
  getOriginalBranchId: () => string | null;

  // Legacy compatibility
  isProfileComplete: () => boolean;
  hasCenterApplication: () => boolean;
}

/**
 * 사용자 데이터 가져오기
 *
 * Server Action (getCurrentUser) 호출로 Supabase 세션 + DB 쿼리
 */
async function fetchUserData(): Promise<UserData | null> {
  try {
    const data = await getCurrentUser();
    if (!data) {
      return null;
    }
    return data;
  } catch (error) {
    console.error("[User Store] Fetch error:", error);
    void diagnoseActionFailure("user-store-getCurrentUser", error);
    return null;
  }
}

/**
 * User Store
 *
 * Supabase 세션이 단일 진실 공급원
 * 페이지 새로고침 시 Server Action으로 자동 복원
 */
export const useUserStore = create<UserStore>()((set, get) => ({
  // Initial state
  user: null,
  isLoading: false,
  isInitialized: false,
  error: null,

  // Basic setters
  setUser: (user) => set({ user }),
  setLoading: (isLoading) => set({ isLoading }),
  setInitialized: (isInitialized) => set({ isInitialized }),
  setError: (error) => set({ error }),
  signOut: async () => {
    try {
      // 1. Call auth service to clear Supabase session
      await authSignOut();

      // 2. Clear user store state
      set({
        user: null,
        isInitialized: false,
        error: null,
      });

      console.log("[User Store] Signed out successfully");
    } catch (error) {
      console.error("[User Store] Sign out error:", error);
      throw error;
    }
  },

  // Refresh user data from DB via Server Action
  refreshUserData: async () => {
    const { user: currentUser, setUser, setLoading, setError, setInitialized, isInitialized: wasInitialized } = get();

    try {
      // 최초 로드 시에만 isLoading 표시 (탭 전환 등 재조회 시 깜빡임 방지)
      if (!wasInitialized) {
        setLoading(true);
      }
      setError(null);

      const userData = await fetchUserData();

      if (!userData) {
        setUser(null);
        setInitialized(true);
        return;
      }

      setUser(userData);
      setInitialized(true);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "사용자 정보를 불러오는 중 오류가 발생했습니다.";
      setError(errorMessage);
      setInitialized(true);
      console.error("[User Store] Error:", err);
    } finally {
      setLoading(false);
    }
  },

  // Clear all user data
  clearUserData: () => {
    set({
      user: null,
      error: null,
      isInitialized: false,
    });
  },

  // === Computed Helpers ===

  isAuthenticated: () => {
    const { user } = get();
    return !!user;
  },

  hasRole: (role: UserRole) => {
    const { user } = get();
    return user?.role === role;
  },

  isCenterAdmin: () => {
    const { user } = get();
    return user?.is_center_admin ?? false;
  },

  isBranchAdmin: () => {
    const { user } = get();
    return user?.is_branch_admin ?? false;
  },

  getCenterId: () => {
    const { user } = get();
    return user?.center_id ?? null;
  },

  getBranchId: () => {
    const { user } = get();
    return user?.branch_id ?? null;
  },

  getUserId: () => {
    const { user } = get();
    return user?.id ?? null;
  },

  // === Impersonation Helpers (v2.0) ===

  isImpersonating: () => {
    const { user } = get();
    return user?._impersonating ?? false;
  },

  getOriginalCenterId: () => {
    const { user } = get();
    if (!user) return null;
    return user._impersonating ? user._original_center_id ?? null : user.center_id;
  },

  getOriginalBranchId: () => {
    const { user } = get();
    if (!user) return null;
    return user._impersonating ? user._original_branch_id ?? null : user.branch_id;
  },

  // === Legacy Compatibility ===

  isProfileComplete: () => {
    const { user } = get();
    if (!user) return false;

    const hasFullName = user.full_name && user.full_name.trim().length > 0;
    const hasPhoneNumber =
      user.phone_number && user.phone_number.trim().length > 0;

    return !!(hasFullName && hasPhoneNumber);
  },

  hasCenterApplication: () => {
    const { user } = get();
    return !!user?.center_id;
  },
}));

// ========== 추가 Export: 비반응형 클래스 ==========

/**
 * 클라이언트 권한 체크 헬퍼 (비반응형)
 *
 * 컴포넌트 외부에서 사용 (이벤트 핸들러, 유틸 함수, 서비스 함수 등)
 */
export class ClientAuth {
  static getCurrentUser() {
    const store = useUserStore.getState();
    return store.user;
  }

  /** @deprecated Use getCurrentUser() instead */
  static getPermissionData() {
    return this.getCurrentUser();
  }

  static isAuthenticated() {
    return !!this.getCurrentUser();
  }

  static hasRole(role: string) {
    const user = this.getCurrentUser();
    return user?.role === role;
  }

  static getUserId() {
    const user = this.getCurrentUser();
    return user?.id ?? null;
  }

  static getCenterId() {
    const user = this.getCurrentUser();
    return user?.center_id ?? null;
  }

  static getBranchId() {
    const user = this.getCurrentUser();
    return user?.branch_id ?? null;
  }

  static isCenterAdmin() {
    const user = this.getCurrentUser();
    return user?.is_center_admin ?? false;
  }

  static isBranchAdmin() {
    const user = this.getCurrentUser();
    return user?.is_branch_admin ?? false;
  }

  static async refreshAuth(forceRefresh = false) {
    const store = useUserStore.getState();
    await store.refreshUserData(forceRefresh);
  }

  static async signOut() {
    const store = useUserStore.getState();
    await store.signOut();
  }

  /** @deprecated Use signOut() instead */
  static clearAuth() {
    const store = useUserStore.getState();
    store.clearUserData();
  }

  static isImpersonating() {
    const store = useUserStore.getState();
    return store.isImpersonating();
  }

  static getOriginalCenterId() {
    const store = useUserStore.getState();
    return store.getOriginalCenterId();
  }

  static getOriginalBranchId() {
    const store = useUserStore.getState();
    return store.getOriginalBranchId();
  }
}
